import { ExternalScriptLoaderService } from './external-script-loader.service';
let mapsLoaderPromise: Promise<void> | null = null;
let mapsMissingKeyWarned = false;

function buildMapsScriptSrc(apiKey: string, callbackName: string): string {
  const params = new URLSearchParams({
    key: apiKey,
    loading: 'async',
    callback: callbackName
  });
  return `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
}

export function loadGoogleMapsScript(apiKey: string | undefined | null): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }

  if ((window as any).google?.maps) {
    return Promise.resolve();
  }

  const normalizedKey = (apiKey ?? '').trim();
  if (!normalizedKey) {
    if (!mapsMissingKeyWarned) {
      mapsMissingKeyWarned = true;
      console.warn('[Google Maps] Missing API key. Set environment.googleMapsApiKey to enable map rendering.');
    }
    return Promise.resolve();
  }

  if (mapsLoaderPromise) {
    return mapsLoaderPromise;
  }

  mapsLoaderPromise = new Promise<void>((resolve, reject) => {
    const callbackName = `__portalGoogleMapsInit_${Date.now()}`;
    const globalWindow = window as any;
    const externalScriptLoader = new ExternalScriptLoaderService();

    const cleanup = () => {
      try {
        delete globalWindow[callbackName];
      } catch {
        globalWindow[callbackName] = undefined;
      }
    };

    globalWindow[callbackName] = () => {
      cleanup();
      resolve();
    };

    const mapsSrc = buildMapsScriptSrc(normalizedKey, callbackName);
    externalScriptLoader
      .load(mapsSrc, {
        id: 'google-maps-sdk',
        async: true,
        defer: true,
        dataAttributes: { 'google-maps-sdk': 'true' },
      })
      .catch(() => {
        cleanup();
        mapsLoaderPromise = null;
        reject(new Error('Unable to load Google Maps script.'));
      });
  });

  return mapsLoaderPromise;
}
